import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";
import { Plus, PenLine, Eye, EyeOff, Trash2, ListChecks, Loader2, RefreshCw } from "lucide-react";
import { listFormsAdmin, upsertForm, deleteForm } from "@/lib/forms.functions";
import {
  listProblemCatalogAdmin,
  upsertProblem,
  deleteProblem,
  syncIntakeMainProblemOptions,
} from "@/lib/problem-catalog.functions";
import { RouteError, RouteNotFound } from "@/components/route-boundaries";

const listQ = () => queryOptions({ queryKey: ["admin", "forms"], queryFn: () => listFormsAdmin() });
const catalogQ = () =>
  queryOptions({ queryKey: ["admin", "problem_catalog"], queryFn: () => listProblemCatalogAdmin() });

export const Route = createFileRoute("/_authenticated/admin/forms")({
  loader: async ({ context }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(listQ()),
      context.queryClient.ensureQueryData(catalogQ()),
    ]);
  },
  errorComponent: ({ error }) => <p className="p-6 text-muted-foreground">{String(error)}</p>,
  notFoundComponent: RouteNotFound,
  component: FormsAdmin,
});

function FormsAdmin() {
  const [tab, setTab] = useState<"forms" | "problems">("forms");
  return (
    <div>
      <header className="mb-6">
        <p className="kicker text-modern-orange">إدارة المحتوى</p>
        <h1 className="mt-1 font-display text-3xl font-extrabold text-foreground">النماذج</h1>
      </header>
      <div className="mb-6 flex flex-wrap gap-2 border-b border-border/60">
        {(
          [
            { id: "forms", label: "النماذج" },
            { id: "problems", label: "بنك المشاكل" },
          ] as const
        ).map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`-mb-px rounded-t-xl px-4 py-2 font-display text-sm font-bold ${
              tab === t.id
                ? "bg-maroon-100 text-maroon-500"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === "forms" ? <FormsTab /> : <ProblemCatalogTab />}
    </div>
  );
}

function FormsTab() {
  const { data } = useSuspenseQuery(listQ());
  const qc = useQueryClient();
  const del = useServerFn(deleteForm);
  const [creating, setCreating] = useState(false);

  async function handleDelete(id: string) {
    if (!confirm("حذف هذا النموذج (وسجّلات إجاباته)؟")) return;
    try { await del({ data: { id } }); toast.success("تم الحذف"); qc.invalidateQueries({ queryKey: ["admin", "forms"] }); }
    catch (e: any) { toast.error(e?.message ?? "فشل الحذف"); }
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-end">
        <button
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2 font-display text-sm font-bold text-primary-foreground shadow-soft hover:bg-primary-hover"
        >
          <Plus className="h-4 w-4" /> نموذج جديد
        </button>
      </div>

      {data.length === 0 ? (
        <div className="rounded-ds-lg border border-dashed border-border bg-card p-12 text-center">
          <ListChecks className="mx-auto h-10 w-10 text-muted-foreground" />
          <p className="mt-4 font-display text-lg font-bold text-foreground">لا يوجد نماذج</p>
          <p className="mt-2 text-sm text-muted-foreground">ابدأ بإنشاء نموذج جديد لجمع البيانات.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl bg-card shadow-soft">
          <table className="w-full text-sm">
            <thead className="bg-background/70 text-xs text-muted-foreground">
              <tr>
                <th className="p-4 text-start font-semibold">العنوان</th>
                <th className="p-4 text-start font-semibold">الفئة</th>
                <th className="p-4 text-start font-semibold">الجمهور</th>
                <th className="p-4 text-start font-semibold">الإجابات</th>
                <th className="p-4 text-start font-semibold">الحالة</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {data.map((f: any) => (
                <tr key={f.id} className="border-t border-border/60 hover:bg-background/40">
                  <td className="p-4">
                    <p className="font-display font-bold text-foreground">{f.title_ar}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">/{f.slug}</p>
                  </td>
                  <td className="p-4 text-muted-foreground">{f.category ?? "—"}</td>
                  <td className="p-4 text-muted-foreground">{audienceLabel(f.audience)}</td>
                  <td className="p-4 text-muted-foreground">{f.submission_count}</td>
                  <td className="p-4">
                    <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold ${f.is_active ? "bg-soft-green/30 text-foreground" : "bg-muted text-muted-foreground"}`}>
                      {f.is_active ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                      {f.is_active ? "منشور" : "مسودة"}
                    </span>
                  </td>
                  <td className="p-4 text-end">
                    <div className="inline-flex gap-1">
                      <Link to="/admin/forms/$id" params={{ id: f.id }} className="rounded-full p-2 text-muted-foreground hover:bg-accent/20 hover:text-foreground">
                        <PenLine className="h-4 w-4" />
                      </Link>
                      <button onClick={() => handleDelete(f.id)} className="rounded-full p-2 text-muted-foreground hover:bg-destructive/20 hover:text-destructive">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && <NewFormDialog onClose={() => setCreating(false)} />}
    </div>
  );
}

function audienceLabel(a: string) {
  return a === "public" ? "عام" : a === "staff" ? "الفريق" : "العملاء";
}

function NewFormDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const create = useServerFn(upsertForm);
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [audience, setAudience] = useState<"client" | "staff" | "public">("client");
  const [busy, setBusy] = useState(false);

  async function submit() {
    setBusy(true);
    try {
      const { id } = await create({
        data: {
          title_ar: title,
          slug: slug || slugify(title),
          description_ar: "",
          audience,
          category: null,
          schema: { fields: [] },
          is_active: false,
        },
      });
      toast.success("تم الإنشاء");
      qc.invalidateQueries({ queryKey: ["admin", "forms"] });
      navigate({ to: "/admin/forms/$id", params: { id } });
    } catch (e: any) { toast.error(e?.message ?? "فشل"); } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/30 backdrop-blur-sm p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-ds-lg bg-card p-6 shadow-lift">
        <h2 className="font-display text-xl font-extrabold text-foreground">نموذج جديد</h2>
        <div className="mt-5 space-y-4">
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-muted-foreground">العنوان</span>
            <input value={title} onChange={(e) => setTitle(e.target.value)} className="w-full rounded-xl border border-input bg-background px-3 py-2 outline-none focus:border-ring" />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-muted-foreground">Slug</span>
            <input value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase())} className="w-full rounded-xl border border-input bg-background px-3 py-2 font-mono text-sm outline-none focus:border-ring" />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-muted-foreground">الجمهور</span>
            <select value={audience} onChange={(e) => setAudience(e.target.value as any)} className="w-full rounded-xl border border-input bg-background px-3 py-2">
              <option value="client">العملاء (مسجّلن)</option>
              <option value="staff">الفريق</option>
              <option value="public">عام (بدون تسجيل)</option>
            </select>
          </label>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-full border border-input px-4 py-2 text-sm font-semibold text-foreground hover:bg-accent/15">إلغاء</button>
          <button onClick={submit} disabled={busy || !title} className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2 font-display text-sm font-bold text-primary-foreground shadow-soft hover:bg-primary-hover disabled:opacity-50">
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} إنشاء
          </button>
        </div>
      </div>
    </div>
  );
}

function slugify(s: string) {
  return s.trim().toLowerCase().replace(/[^\p{L}\p{N}]+/gu, "-").replace(/^-+|-+$/g, "").slice(0, 60) || `form-${Date.now()}`;
}

// ---------------- Problem catalog sub-tab (Phase 3) ----------------

function ProblemCatalogTab() {
  const { data } = useSuspenseQuery(catalogQ());
  const qc = useQueryClient();
  const upsert = useServerFn(upsertProblem);
  const del = useServerFn(deleteProblem);
  const sync = useServerFn(syncIntakeMainProblemOptions);
  const [editing, setEditing] = useState<any | null>(null);
  const [syncing, setSyncing] = useState(false);

  async function toggleActive(row: any) {
    try {
      await upsert({ data: { ...row, is_active: !row.is_active } });
      qc.invalidateQueries({ queryKey: ["admin", "problem_catalog"] });
    } catch (e: any) { toast.error(e?.message ?? "فشل التحديث"); }
  }

  async function handleDelete(id: string) {
    if (!confirm("حذف هذه المشكلة من البنك؟")) return;
    try {
      await del({ data: { id } });
      toast.success("تم الحذف");
      qc.invalidateQueries({ queryKey: ["admin", "problem_catalog"] });
    } catch (e: any) { toast.error(e?.message ?? "فشل الحذف"); }
  }

  async function handleSync() {
    setSyncing(true);
    try {
      const res = await sync();
      toast.success(`تمت مزامنة ${res.option_count} خيارًا في نموذج الاستشارة.`);
    } catch (e: any) { toast.error(e?.message ?? "فشلت المزامنة"); }
    finally { setSyncing(false); }
  }

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          بنك المشاكل يظهر في السؤال المحوري بنموذج الاستشارة. بعد الإضافة أو التعديل استخدم "مزامنة الخيارات" لتحديث النموذج.
        </p>
        <div className="flex gap-2">
          <button
            onClick={handleSync}
            disabled={syncing}
            className="inline-flex items-center gap-2 rounded-full border border-input bg-background px-4 py-2 font-display text-sm font-bold text-foreground hover:bg-accent/15 disabled:opacity-50"
          >
            {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            مزامنة الخيارات مع النموذج
          </button>
          <button
            onClick={() => setEditing({})}
            className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2 font-display text-sm font-bold text-primary-foreground shadow-soft hover:bg-primary-hover"
          >
            <Plus className="h-4 w-4" /> مشكلة جديدة
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl bg-card shadow-soft">
        <table className="w-full text-sm">
          <thead className="bg-background/70 text-xs text-muted-foreground">
            <tr>
              <th className="p-4 text-start font-semibold">الترتيب</th>
              <th className="p-4 text-start font-semibold">التسمية (لغة الأهل)</th>
              <th className="p-4 text-start font-semibold">الـSlug</th>
              <th className="p-4 text-start font-semibold">الفئات العمرية</th>
              <th className="p-4 text-start font-semibold">قسم التشخيص</th>
              <th className="p-4 text-start font-semibold">الحالة</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {data.map((r: any) => (
              <tr key={r.id} className="border-t border-border/60 hover:bg-background/40">
                <td className="p-4 text-muted-foreground">{r.order}</td>
                <td className="p-4"><p className="font-display font-bold text-foreground">{r.label_ar}</p></td>
                <td className="p-4 font-mono text-xs text-muted-foreground">{r.slug}</td>
                <td className="p-4 text-xs text-muted-foreground">
                  {(r.age_bands ?? []).length ? (r.age_bands as string[]).join("، ") : "—"}
                </td>
                <td className="p-4 font-mono text-xs text-muted-foreground">{r.diagnostic_section_ref ?? "—"}</td>
                <td className="p-4">
                  <button
                    onClick={() => toggleActive(r)}
                    className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold ${
                      r.is_active ? "bg-soft-green/30 text-foreground" : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {r.is_active ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                    {r.is_active ? "مفعّلة" : "معطّلة"}
                  </button>
                </td>
                <td className="p-4 text-end">
                  <div className="inline-flex gap-1">
                    <button onClick={() => setEditing(r)} className="rounded-full p-2 text-muted-foreground hover:bg-accent/20 hover:text-foreground">
                      <PenLine className="h-4 w-4" />
                    </button>
                    <button onClick={() => handleDelete(r.id)} className="rounded-full p-2 text-muted-foreground hover:bg-destructive/20 hover:text-destructive">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {data.length === 0 && (
              <tr><td colSpan={7} className="p-12 text-center text-muted-foreground">لا توجد مشاكل مسجّلة بعد.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && <ProblemDialog row={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function ProblemDialog({ row, onClose }: { row: any; onClose: () => void }) {
  const qc = useQueryClient();
  const upsert = useServerFn(upsertProblem);
  const [slug, setSlug] = useState<string>(row.slug ?? "");
  const [labelAr, setLabelAr] = useState<string>(row.label_ar ?? "");
  const [order, setOrder] = useState<number>(row.order ?? 0);
  const [ageBands, setAgeBands] = useState<string>((row.age_bands ?? []).join(", "));
  const [sectionRef, setSectionRef] = useState<string>(row.diagnostic_section_ref ?? "");
  const [pillars, setPillars] = useState<string>((row.suggested_pillars ?? []).join(", "));
  const [isActive, setIsActive] = useState<boolean>(row.is_active ?? true);
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!slug || !labelAr) { toast.error("Slug والتسمية مطلوبان."); return; }
    setBusy(true);
    try {
      await upsert({
        data: {
          id: row.id,
          slug,
          label_ar: labelAr,
          order,
          age_bands: ageBands.split(",").map((s) => s.trim()).filter(Boolean),
          diagnostic_section_ref: sectionRef.trim() || null,
          suggested_pillars: pillars.split(",").map((s) => s.trim()).filter(Boolean),
          is_active: isActive,
        },
      });
      toast.success("تم الحفظ");
      qc.invalidateQueries({ queryKey: ["admin", "problem_catalog"] });
      onClose();
    } catch (e: any) { toast.error(e?.message ?? "فشل"); }
    finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/30 backdrop-blur-sm p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg rounded-ds-lg bg-card p-6 shadow-lift">
        <h2 className="font-display text-xl font-extrabold text-foreground">{row.id ? "تعديل مشكلة" : "مشكلة جديدة"}</h2>
        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <label className="block sm:col-span-2">
            <span className="mb-1 block text-xs font-semibold text-muted-foreground">التسمية (بلغة الأهل)</span>
            <input value={labelAr} onChange={(e) => setLabelAr(e.target.value)} className="w-full rounded-xl border border-input bg-background px-3 py-2 outline-none focus:border-ring" />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-muted-foreground">Slug</span>
            <input value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase())} className="w-full rounded-xl border border-input bg-background px-3 py-2 font-mono text-sm outline-none focus:border-ring" />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-muted-foreground">الترتيب</span>
            <input type="number" value={order} onChange={(e) => setOrder(Number(e.target.value) || 0)} className="w-full rounded-xl border border-input bg-background px-3 py-2 outline-none focus:border-ring" />
          </label>
          <label className="block sm:col-span-2">
            <span className="mb-1 block text-xs font-semibold text-muted-foreground">الفئات العمرية (مفصولة بفواصل)</span>
            <input value={ageBands} onChange={(e) => setAgeBands(e.target.value)} className="w-full rounded-xl border border-input bg-background px-3 py-2 outline-none focus:border-ring" placeholder="3–5 سنوات, 6–9 سنوات" />
          </label>
          <label className="block sm:col-span-2">
            <span className="mb-1 block text-xs font-semibold text-muted-foreground">مرجع قسم التشخيص (section id داخل consultation-intake)</span>
            <input value={sectionRef} onChange={(e) => setSectionRef(e.target.value)} className="w-full rounded-xl border border-input bg-background px-3 py-2 font-mono text-sm outline-none focus:border-ring" placeholder="diag_hits" />
          </label>
          <label className="block sm:col-span-2">
            <span className="mb-1 block text-xs font-semibold text-muted-foreground">Pillars المقترحة (slugs مفصولة بفواصل — للاستخدام في المرحلة الخامسة)</span>
            <input value={pillars} onChange={(e) => setPillars(e.target.value)} className="w-full rounded-xl border border-input bg-background px-3 py-2 font-mono text-sm outline-none focus:border-ring" placeholder="emotion_regulation, executive_functions" />
          </label>
          <label className="flex items-center gap-2 sm:col-span-2">
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            <span className="text-sm font-semibold text-foreground">مفعّلة (تظهر في نموذج الحجز)</span>
          </label>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-full border border-input px-4 py-2 text-sm font-semibold text-foreground hover:bg-accent/15">إلغاء</button>
          <button onClick={submit} disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2 font-display text-sm font-bold text-primary-foreground shadow-soft hover:bg-primary-hover disabled:opacity-50">
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} حفظ
          </button>
        </div>
      </div>
    </div>
  );
}
